import { formatDistanceToNowStrict, parseISO } from "date-fns";
import {
  Bot,
  Check,
  Copy,
  Info,
  PenLine,
  RefreshCw,
  Search,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Badge, type StatusType } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { CompanyLogo } from "../../../../components/ui/CompanyLogo";
import { Skeleton } from "../../../../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { cn } from "../../../../lib/utils";
import { useMcpStatus } from "../../../../services/mcp/mcpHooks";
import type {
  McpActivityItem,
  McpConnection,
  McpScopeInfo,
  McpStatusScope,
} from "../../../../services/mcp/mcp.types";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";

/* ------------------------------------------------------------------ */
/* Static content                                                      */
/* ------------------------------------------------------------------ */

const DEFAULT_SCOPES: McpScopeInfo[] = [
  {
    name: "hyperengage:read",
    title: "Read customer data",
    description: "View accounts, contacts, usage, conversations, insights and signals",
  },
  {
    name: "hyperengage:intelligence",
    title: "Hyperengage AI analysis",
    description: "Ask Hyperengage's AI analyst (uses workspace AI credits)",
  },
  {
    name: "hyperengage:write",
    title: "Create records",
    description: "Create notes, tasks and documents (never edits or deletes)",
  },
];

const SCOPE_ICONS: Record<string, LucideIcon> = {
  "hyperengage:read": Search,
  "hyperengage:intelligence": Sparkles,
  "hyperengage:write": PenLine,
};

const EXAMPLE_QUESTIONS = [
  "What should I focus on today?",
  "Brief me on Acme before my call",
  "Which renewals in the next 90 days have red flags?",
  "Write a QBR for Acme and save it",
];

const URL_PLACEHOLDER = "<connector URL>";

type SetupClient = {
  id: string;
  label: string;
  steps: string[];
  command?: (url: string) => string;
  commandIntro?: string;
  commandOutro?: string;
};

const SETUP_CLIENTS: SetupClient[] = [
  {
    id: "claude",
    label: "Claude (claude.ai / Desktop)",
    steps: [
      "Open Settings → Connectors.",
      "Click Add custom connector and paste the connector URL above.",
      "Sign in with your Hyperengage account.",
      "Choose the workspace you want to connect.",
      "Review and allow the requested permissions.",
    ],
  },
  {
    id: "chatgpt",
    label: "ChatGPT",
    steps: [
      "Open Settings → Connectors and turn on Developer mode.",
      "Click Create and paste the connector URL above.",
      "Complete the OAuth sign-in with your Hyperengage account.",
      "Choose the workspace you want to connect and allow the permissions.",
    ],
  },
  {
    id: "claude-code",
    label: "Claude Code",
    steps: [],
    commandIntro: "Add the server from your terminal:",
    command: (url) => `claude mcp add --transport http hyperengage ${url}`,
    commandOutro: "Then run /mcp inside Claude Code and choose hyperengage to authenticate in your browser.",
  },
  {
    id: "cursor",
    label: "Cursor",
    steps: [
      "Open Cursor Settings → MCP.",
      "Click Add server and choose the HTTP (streamable) transport.",
      "Paste the connector URL above and save.",
      "Click Connect / Login when prompted and sign in with Hyperengage.",
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

type ClientDisplay = { name: string; domain?: string };

const resolveClient = (clientId: string | null | undefined, clientName: string | null | undefined): ClientDisplay => {
  const haystack = `${clientId ?? ""} ${clientName ?? ""}`.toLowerCase();
  if (haystack.includes("claude code") || haystack.includes("claude-code")) return { name: "Claude Code", domain: "claude.ai" };
  if (haystack.includes("claude.ai") || haystack.includes("anthropic") || /\bclaude\b/.test(haystack)) {
    return { name: "Claude", domain: "claude.ai" };
  }
  if (haystack.includes("chatgpt") || haystack.includes("openai")) return { name: "ChatGPT", domain: "chatgpt.com" };
  if (haystack.includes("cursor")) return { name: "Cursor", domain: "cursor.com" };
  if (haystack.includes("localhost") || haystack.includes("127.0.0.1")) return { name: clientName || "Claude Code", domain: "claude.ai" };
  if (clientName) return { name: clientName };
  if (clientId) {
    try {
      return { name: new URL(clientId).hostname };
    } catch {
      return { name: clientId };
    }
  }
  return { name: "Unknown client" };
};

const relativeTime = (value: string | null | undefined) => {
  if (!value) return "—";
  try {
    return formatDistanceToNowStrict(parseISO(value), { addSuffix: true });
  } catch {
    return value;
  }
};

const absoluteTime = (value: string | null | undefined) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const ACTIVITY_STATUS: Record<string, { label: string; status: StatusType }> = {
  ok: { label: "OK", status: "active" },
  error: { label: "Error", status: "warning" },
  internal_error: { label: "Internal error", status: "error" },
};

const SectionHeading = ({ title, description }: { title: string; description?: string }) => (
  <div className="mb-4">
    <h2 className="text-[13px] font-medium text-foreground">{title}</h2>
    {description ? <p className="mt-1 text-[11px] text-muted-foreground">{description}</p> : null}
  </div>
);

const EmptyCard = ({ children }: { children: ReactNode }) => (
  <div className="rounded-lg border border-border-strong bg-card px-4 py-8 text-center text-[13px] text-muted-foreground">
    {children}
  </div>
);

const ClientCell = ({ clientId, clientName }: { clientId?: string | null; clientName?: string | null }) => {
  const client = resolveClient(clientId, clientName);
  return (
    <div className="flex items-center gap-2">
      {client.domain ? (
        <CompanyLogo domain={client.domain} name={client.name} className="h-5 w-5 rounded" />
      ) : (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
          <Bot className="h-3 w-3" />
        </span>
      )}
      <span className="truncate text-[13px] text-foreground" title={clientId ?? undefined}>
        {client.name}
      </span>
    </div>
  );
};

const useCopy = () => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  useEffect(() => {
    if (!copiedKey) return undefined;
    const t = window.setTimeout(() => setCopiedKey(null), 1500);
    return () => window.clearTimeout(t);
  }, [copiedKey]);

  const copy = async (key: string, value: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success(successMessage);
    } catch {
      toast.error("Unable to copy to clipboard.");
    }
  };
  return { copiedKey, copy };
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const McpConnectorsPage = () => {
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  const [activityScope, setActivityScope] = useState<McpStatusScope>("me");
  const { copiedKey, copy } = useCopy();

  const { data, isLoading, isError, isFetching, refetch } = useMcpStatus({
    workspaceId,
    scope: activityScope,
  });

  const status = data?.available ? data.status : null;
  const enabled = Boolean(status?.enabled);
  const serverUrl = enabled ? status?.server_url || null : null;
  const scopes = status?.scopes?.length ? status.scopes : DEFAULT_SCOPES;
  const canViewWorkspace = Boolean(enabled && status?.can_view_workspace);

  // If the backend refused workspace scope (403) fall back to "me".
  useEffect(() => {
    if (data?.available && data.fellBackToMe && activityScope !== "me") {
      setActivityScope("me");
    }
  }, [data, activityScope]);

  const scopeTitleByName = useMemo(() => {
    const map: Record<string, string> = {};
    scopes.forEach((s) => {
      map[s.name] = s.title;
    });
    return map;
  }, [scopes]);

  const connections: McpConnection[] = status?.connections ?? [];
  const activity: McpActivityItem[] = status?.recent_activity ?? [];
  const showUserColumn = status?.scope === "workspace";
  const urlForSteps = serverUrl ?? URL_PLACEHOLDER;

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div>
          <Skeleton className="h-6 w-56" />
          <Skeleton className="mt-2 h-4 w-full" />
          <Skeleton className="mt-1.5 h-4 w-3/4" />
        </div>
        <Skeleton className="h-20 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2.5">
          <h1 className="text-lg font-medium text-foreground">AI Connectors (MCP)</h1>
          {!isError ? (
            <Badge variant="statusPill" status={enabled ? "active" : "open"}>
              {enabled ? "Enabled" : "Not enabled"}
            </Badge>
          ) : null}
        </div>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Use Hyperengage inside Claude, ChatGPT and your coding tools — one connection gives your AI
          assistant your accounts, usage, conversations, health and signals.
        </p>
      </div>

      {isError ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-border-strong bg-card px-4 py-8 text-center">
          <p className="text-[13px] text-muted-foreground">Unable to load AI connector status.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn(isFetching && "animate-spin")} />
            Retry
          </Button>
        </div>
      ) : null}

      {!isError && !enabled ? (
        <div className="flex gap-3 rounded-lg border border-border-strong bg-card p-4">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div>
            <p className="text-[13px] font-medium text-foreground">AI connectors aren&apos;t enabled yet</p>
            <p className="mt-1 text-[12px] text-muted-foreground">
              MCP isn&apos;t turned on for this environment yet. The setup guide below shows how connecting
              will work once it&apos;s available.
            </p>
          </div>
        </div>
      ) : null}

      {/* Connector URL */}
      <div className={cn(!enabled && "opacity-60")}>
        <SectionHeading
          title="Connector URL"
          description="Paste this URL into your AI assistant to add Hyperengage as a connector."
        />
        <div className="flex items-center gap-2 rounded-lg border border-border-strong bg-card p-3">
          <code className="min-w-0 flex-1 truncate rounded-[5px] bg-muted px-3 py-1.5 font-mono text-[13px] text-foreground">
            {serverUrl ?? "Available once MCP is enabled"}
          </code>
          <Button
            variant="outline"
            size="sm"
            disabled={!serverUrl}
            onClick={() => serverUrl && copy("url", serverUrl, "Connector URL copied.")}
          >
            {copiedKey === "url" ? <Check /> : <Copy />}
            Copy
          </Button>
        </div>
      </div>

      {/* Setup guide */}
      <div className={cn(!enabled && "opacity-60")}>
        <SectionHeading
          title="Set up your assistant"
          description="You'll be asked to pick a workspace. To use another workspace, add the connector again."
        />
        <div className="rounded-lg border border-border-strong bg-card p-4">
          <Tabs defaultValue={SETUP_CLIENTS[0].id}>
            <TabsList className="h-auto flex-wrap justify-start">
              {SETUP_CLIENTS.map((client) => (
                <TabsTrigger key={client.id} value={client.id} className="text-[13px]">
                  {client.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {SETUP_CLIENTS.map((client) => {
              const { steps } = client;
              const command = client.command?.(urlForSteps);
              return (
                <TabsContent key={client.id} value={client.id} className="mt-4">
                  {client.commandIntro ? (
                    <p className="mb-2 text-[13px] text-foreground">{client.commandIntro}</p>
                  ) : null}
                  {command ? (
                    <div className="mb-3 flex items-center gap-2 rounded-[5px] bg-muted px-3 py-2">
                      <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap font-mono text-[12px] text-foreground">
                        {command}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Copy command"
                        disabled={!serverUrl}
                        onClick={() => copy(`cmd-${client.id}`, command, "Command copied.")}
                      >
                        {copiedKey === `cmd-${client.id}` ? <Check /> : <Copy />}
                      </Button>
                    </div>
                  ) : null}
                  {client.commandOutro ? (
                    <p className="text-[13px] text-muted-foreground">{client.commandOutro}</p>
                  ) : null}
                  {steps.length ? (
                    <ol className="space-y-2">
                      {steps.map((step, index) => (
                        <li key={step} className="flex gap-2.5 text-[13px] text-foreground">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-muted-foreground">
                            {index + 1}
                          </span>
                          <span className="pt-px">{step}</span>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </div>

      {/* Capabilities */}
      <div>
        <SectionHeading
          title="What your assistant can do"
          description="AI analysis uses your workspace's AI credits. Writes are create-only — your assistant never edits or deletes data."
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {scopes.map((scope) => {
            const Icon = SCOPE_ICONS[scope.name] ?? Bot;
            return (
              <div key={scope.name} className="rounded-lg border border-border-strong bg-card p-4">
                <span className="mb-2 flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </span>
                <p className="text-[13px] font-medium text-foreground">{scope.title}</p>
                <p className="mt-1 text-[12px] text-muted-foreground">{scope.description}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-3 rounded-lg border border-border-strong bg-card p-4">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Try asking
          </p>
          <ul className="space-y-1.5">
            {EXAMPLE_QUESTIONS.map((q) => (
              <li key={q} className="text-[13px] text-foreground">
                “{q}”
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Connections */}
      {!isError && enabled ? (
        <div>
          <SectionHeading
            title="Your connections"
            description="Assistants that have used Hyperengage on your behalf. First used is when the assistant first connected."
          />
          {connections.length === 0 ? (
            <EmptyCard>No assistants connected yet — follow the steps above.</EmptyCard>
          ) : (
            <div className="rounded-lg border border-border-strong bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-10 text-[12px]">Client</TableHead>
                    <TableHead className="h-10 text-[12px]">Permissions</TableHead>
                    <TableHead className="h-10 text-[12px]">First used</TableHead>
                    <TableHead className="h-10 text-[12px]">Last used</TableHead>
                    <TableHead className="h-10 text-right text-[12px]">Calls (7d)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {connections.map((conn) => (
                    <TableRow key={conn.client_id} className="hover:bg-transparent">
                      <TableCell className="py-2.5">
                        <ClientCell clientId={conn.client_id} clientName={conn.client_name} />
                      </TableCell>
                      <TableCell className="py-2.5">
                        {conn.scopes?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {conn.scopes.map((s) => (
                              <Badge key={s} variant="secondary" className="h-5 px-1.5 text-[11px] font-normal">
                                {scopeTitleByName[s] ?? s}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[13px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell
                        className="whitespace-nowrap py-2.5 text-[13px] text-muted-foreground"
                        title={absoluteTime(conn.first_used_at)}
                      >
                        {relativeTime(conn.first_used_at)}
                      </TableCell>
                      <TableCell
                        className="whitespace-nowrap py-2.5 text-[13px] text-muted-foreground"
                        title={absoluteTime(conn.last_used_at)}
                      >
                        {relativeTime(conn.last_used_at)}
                      </TableCell>
                      <TableCell className="py-2.5 text-right text-[13px] text-foreground">
                        {conn.calls_7d.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ) : null}

      {/* Recent activity */}
      {!isError && enabled ? (
        <div>
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-[13px] font-medium text-foreground">Recent activity</h2>
              <p className="mt-1 text-[11px] text-muted-foreground">
                The latest tool calls made through the Hyperengage connector.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isFetching ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
              ) : null}
              {canViewWorkspace ? (
                <Tabs value={activityScope} onValueChange={(v) => setActivityScope(v as McpStatusScope)}>
                  <TabsList>
                    <TabsTrigger value="me" className="text-[12px]">Me</TabsTrigger>
                    <TabsTrigger value="workspace" className="text-[12px]">Whole workspace</TabsTrigger>
                  </TabsList>
                </Tabs>
              ) : null}
            </div>
          </div>
          {activity.length === 0 ? (
            <EmptyCard>No activity yet.</EmptyCard>
          ) : (
            <div className="rounded-lg border border-border-strong bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-10 text-[12px]">Time</TableHead>
                    <TableHead className="h-10 text-[12px]">Tool</TableHead>
                    <TableHead className="h-10 text-[12px]">Client</TableHead>
                    <TableHead className="h-10 text-[12px]">Status</TableHead>
                    {showUserColumn ? <TableHead className="h-10 text-[12px]">User</TableHead> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activity.map((item) => {
                    const st = ACTIVITY_STATUS[item.status] ?? { label: item.status, status: "info" as StatusType };
                    return (
                      <TableRow key={item.id} className="hover:bg-transparent">
                        <TableCell
                          className="whitespace-nowrap py-2.5 text-[13px] text-muted-foreground"
                          title={absoluteTime(item.created_at)}
                        >
                          {relativeTime(item.created_at)}
                        </TableCell>
                        <TableCell className="py-2.5 font-mono text-[12px] text-foreground">{item.tool}</TableCell>
                        <TableCell className="py-2.5">
                          <ClientCell clientName={item.client_name} />
                        </TableCell>
                        <TableCell className="py-2.5">
                          <Badge variant="statusPill" status={st.status}>{st.label}</Badge>
                        </TableCell>
                        {showUserColumn ? (
                          <TableCell className="py-2.5 text-[13px] text-muted-foreground">
                            {item.user_email || "—"}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ) : null}

      {/* Disconnecting */}
      <div>
        <SectionHeading title="Disconnecting" />
        <div className="rounded-lg border border-border-strong bg-card p-4 text-[13px] text-muted-foreground">
          To disconnect, remove the Hyperengage connector in your assistant&apos;s settings. Access also
          expires automatically after the session period.
        </div>
      </div>
    </div>
  );
};

export default McpConnectorsPage;
